// ---------------------------------------------------------------------------
// Enterprise module — Runbooks management page
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { DataTable, type Column } from '../components/DataTable';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Local types (mirrors @noxverify/runbooks types for UI consumption)
// ---------------------------------------------------------------------------

interface RunbookStep {
  order: number;
  description: string;
  command?: string;
  verification: string;
  rollback: string;
}

interface Runbook {
  id: string;
  name: string;
  description: string;
  category: string;
  steps: RunbookStep[];
  severity: string;
  estimatedTime: number;
}

interface RunbookExecution {
  id: string;
  runbookId: string;
  executedBy: string;
  startedAt: string;
  completedAt: string | null;
  status: string;
  stepResults: Array<{
    stepOrder: number;
    status: string;
    notes?: string;
    completedAt: string;
  }>;
}

// ---------------------------------------------------------------------------
// Execution history columns
// ---------------------------------------------------------------------------

function buildExecutionColumns(
  t: ReturnType<typeof useTranslation>['t'],
  f: ReturnType<typeof useFormatters>,
): Column<RunbookExecution>[] {
  return [
    {
      key: 'id',
      header: t('runbooks.history.columns.id'),
      render: (e) => <span className="text-sm font-mono text-ink">{e.id}</span>,
    },
    {
      key: 'runbookId',
      header: t('runbooks.history.columns.runbook'),
      render: (e) => <span className="text-sm text-ink">{e.runbookId}</span>,
    },
    {
      key: 'executedBy',
      header: t('runbooks.history.columns.executedBy'),
      render: (e) => <span className="text-sm text-ink-muted">{e.executedBy}</span>,
    },
    {
      key: 'status',
      header: t('runbooks.history.columns.status'),
      render: (e) => <StatusBadge value={e.status} />,
    },
    {
      key: 'startedAt',
      header: t('runbooks.history.columns.started'),
      render: (e) => (
        <span className="text-sm text-ink-muted tabular-nums font-mono">
          {f.date(e.startedAt, { dateStyle: 'medium', timeStyle: 'short' })}
        </span>
      ),
    },
    {
      key: 'completedAt',
      header: t('runbooks.history.columns.completed'),
      render: (e) => (
        <span className="text-sm text-ink-muted">
          {e.completedAt
            ? f.date(e.completedAt, { dateStyle: 'medium', timeStyle: 'short' })
            : '--'}
        </span>
      ),
    },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type RunbookTab = 'catalog' | 'active' | 'history';

export function RunbooksPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const executionColumns = useMemo(() => buildExecutionColumns(t, f), [t, f]);
  const categoryLabels = t('runbooks.categories', { returnObjects: true }) as Record<string, string>;
  const [runbooks, setRunbooks] = useState<Runbook[]>([]);
  const [executions, setExecutions] = useState<RunbookExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRunbook, setSelectedRunbook] = useState<Runbook | null>(null);
  const [activeTab, setActiveTab] = useState<RunbookTab>('catalog');

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);

    Promise.resolve()
      .then(() => {
        setRunbooks([]);
        setExecutions([]);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : t('runbooks.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadData} />;

  // Group runbooks by category
  const grouped = runbooks.reduce<Record<string, Runbook[]>>((acc, rb) => {
    const key = rb.category;
    if (!acc[key]) acc[key] = [];
    acc[key].push(rb);
    return acc;
  }, {});

  const activeExecutions = executions.filter((e) => e.status === 'in_progress');
  const completedExecutions = executions.filter(
    (e) => e.status !== 'in_progress',
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('runbooks.headings.title')}</h1>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-hairline mb-6">
        <nav className="flex space-x-8" aria-label={t('runbooks.tabsAriaLabel')}>
          {(['catalog', 'active', 'history'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-ink-muted hover:text-ink hover:border-hairline-strong'
              }`}
            >
              {tab === 'catalog' && t('runbooks.tabs.catalog')}
              {tab === 'active' && t('runbooks.tabs.active', { count: activeExecutions.length })}
              {tab === 'history' && t('runbooks.tabs.history')}
            </button>
          ))}
        </nav>
      </div>

      {/* Catalog tab */}
      {activeTab === 'catalog' && (
        <div className="space-y-8">
          {Object.entries(grouped).map(([category, rbs]) => (
            <div key={category}>
              <h2 className="text-lg font-semibold text-ink mb-3">
                {categoryLabels[category] ?? category}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {rbs.map((rb) => (
                  <div
                    key={rb.id}
                    className="border border-hairline rounded-sm p-4 hover:border-primary/40 cursor-pointer transition-colors"
                    onClick={() => setSelectedRunbook(rb)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        setSelectedRunbook(rb);
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-ink">{rb.name}</h3>
                      <StatusBadge value={rb.severity} variant="priority" />
                    </div>
                    <p className="text-sm text-ink-muted mb-3 line-clamp-2">
                      {rb.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-ink-muted">
                      <span>{t('runbooks.catalog.stepsCount', { count: rb.steps.length })}</span>
                      <span>{t('runbooks.catalog.estimatedTime', { minutes: rb.estimatedTime })}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {runbooks.length === 0 && (
            <p className="text-ink-muted text-center py-8">
              {t('runbooks.catalog.empty')}
            </p>
          )}
        </div>
      )}

      {/* Active executions tab */}
      {activeTab === 'active' && (
        <div>
          {activeExecutions.length === 0 ? (
            <p className="text-ink-muted text-center py-8">
              {t('runbooks.active.empty')}
            </p>
          ) : (
            <div className="space-y-4">
              {activeExecutions.map((exec) => {
                const rb = runbooks.find((r) => r.id === exec.runbookId);
                const totalSteps = rb?.steps.length ?? 0;
                const completedSteps = exec.stepResults.length;
                const progressPct =
                  totalSteps > 0
                    ? Math.round((completedSteps / totalSteps) * 100)
                    : 0;

                return (
                  <div
                    key={exec.id}
                    className="border border-hairline rounded-sm p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-ink">
                        {rb?.name ?? exec.runbookId}
                      </h3>
                      <StatusBadge value={exec.status} />
                    </div>
                    <div className="w-full bg-surface-sunken rounded-sm h-2 mb-2">
                      <div
                        className="bg-primary h-2 rounded-sm transition-all"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <p className="text-xs text-ink-muted">
                      {t('runbooks.active.stepProgress', {
                        completed: completedSteps,
                        total: totalSteps,
                      })}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* History tab */}
      {activeTab === 'history' && (
        <DataTable
          columns={executionColumns}
          data={completedExecutions}
          rowKey={(e) => e.id}
          emptyMessage={t('runbooks.history.empty')}
          ariaLabel={t('runbooks.history.ariaLabel')}
        />
      )}

      {/* Runbook detail modal */}
      {selectedRunbook && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedRunbook(null)}
          role="dialog"
          aria-modal="true"
          aria-label={t('runbooks.detail.dialogAria', { name: selectedRunbook.name })}
        >
          <div
            className="bg-surface-raised rounded-sm border border-hairline max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-ink">
                  {selectedRunbook.name}
                </h2>
                <button
                  type="button"
                  onClick={() => setSelectedRunbook(null)}
                  className="text-ink-muted hover:text-ink-muted"
                  aria-label={t('runbooks.detail.closeLabel')}
                >
                  X
                </button>
              </div>
              <p className="text-ink-muted mb-4">
                {selectedRunbook.description}
              </p>
              <div className="flex gap-4 mb-6 text-sm">
                <StatusBadge
                  value={selectedRunbook.severity}
                  variant="priority"
                />
                <span className="text-ink-muted">
                  {t('runbooks.catalog.stepsCount', { count: selectedRunbook.steps.length })}
                </span>
                <span className="text-ink-muted">
                  {t('runbooks.catalog.estimatedTime', { minutes: selectedRunbook.estimatedTime })}
                </span>
              </div>
              <h3 className="font-semibold text-ink mb-3">{t('runbooks.headings.stepsHeading')}</h3>
              <ol className="space-y-3">
                {selectedRunbook.steps.map((step) => (
                  <li
                    key={step.order}
                    className="border border-hairline rounded p-3"
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-surface-sunken rounded-sm flex items-center justify-center text-xs font-medium text-ink-muted">
                        {step.order}
                      </span>
                      <div>
                        <p className="text-sm text-ink">
                          {step.description}
                        </p>
                        {step.command && (
                          <pre className="mt-1 text-xs bg-surface-sunken p-2 rounded text-ink-muted overflow-x-auto">
                            {step.command}
                          </pre>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  className="px-4 py-2 bg-primary text-white text-sm font-medium rounded hover:bg-primary-hover transition-colors"
                  onClick={() => {
                    // In production: api.admin.startRunbook(selectedRunbook.id)
                    setSelectedRunbook(null);
                  }}
                >
                  {t('runbooks.detail.startExecution')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
